import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {memo, useState} from 'react';
import {
  components,
  ControlProps,
  MenuProps,
  OptionProps,
  SingleValueProps,
  ValueContainerProps,
} from 'react-select';

// eslint-disable-next-line wandb/no-deprecated-imports
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import {TeamMemberRole} from '../../MembersTable/TeamMembersTable/utils';
import {RoleOption} from '../../MembersTable/TeamOrOrganizationRoleOption';
import {useOnUserProjectRoleChange} from '../hooks';
import {getRoleLabel, getRoleText, TeamProjectMember} from './utils';

export interface ProjectMembersRoleCellProps {
  projectRoleOptions: ProjectRoleOption[];
  canViewerPerformActions: boolean;
  projectMemberUser: TeamProjectMember;
  projectID: string;
  projectName: string;
  projectOwnerID: string;
  projectTeamName: string;
}

export const ProjectMembersRoleCell = memo(
  ({
    projectRoleOptions,
    projectMemberUser,
    canViewerPerformActions,
    projectID,
    projectName,
    projectOwnerID,
    projectTeamName,
  }: ProjectMembersRoleCellProps) => {
    const isServiceAccount = checkIsServiceAccount(
      projectMemberUser.accountType
    );
    const isProjectRoleDiffersFromTeamRole =
      !isServiceAccount &&
      projectMemberUser.role !== projectMemberUser.teamRole;
    const teamRoleIsViewer =
      projectMemberUser.teamRole === TeamMemberRole.VIEW_ONLY;
    const canUpdateRole =
      canViewerPerformActions && !isServiceAccount && !teamRoleIsViewer;

    const [isOpen, setIsOpen] = useState(false);

    const {onUserProjectRoleChange, isUpdating} = useOnUserProjectRoleChange(
      projectTeamName,
      projectName
    );

    let roleName = getRoleLabel(projectMemberUser);
    if (projectMemberUser.id === projectOwnerID) {
      roleName += ' (Owner)';
    }
    if (isProjectRoleDiffersFromTeamRole) {
      roleName += '*';
    }

    return canUpdateRole ? (
      <Select<ProjectRoleOption, false>
        aria-label="update user role"
        menuIsOpen={isOpen}
        onMenuOpen={() => setIsOpen(true)}
        onMenuClose={() => setIsOpen(false)}
        options={projectRoleOptions}
        formatOptionLabel={(option: ProjectRoleOption | null) => option?.value}
        value={getProjectRoleOptionFromRole(projectMemberUser.role)}
        onChange={(selected: ProjectRoleOption | null) => {
          if (selected?.role && !selected?.isDisabled) {
            onUserProjectRoleChange(
              projectMemberUser.id,
              projectID,
              selected?.role
            );
          }
        }}
        components={{
          Control: (props: ControlProps<ProjectRoleOption, false>) => (
            <components.Control
              {...props}
              className="-ml-2 w-fit bg-transparent shadow-none hover:cursor-pointer hover:shadow-none"
            />
          ),
          ValueContainer: (
            props: ValueContainerProps<ProjectRoleOption, false>
          ) => <components.ValueContainer {...props} className="p-0" />,
          SingleValue: ({
            children,
            ...props
          }: SingleValueProps<ProjectRoleOption, false>) => (
            <components.SingleValue
              {...props}
              className="flex items-center gap-2">
              <ProjectRoleLabel
                isProjectRoleDiffersFromTeamRole={
                  isProjectRoleDiffersFromTeamRole
                }
                teamRoleIsViewer={teamRoleIsViewer}
                roleName={roleName}
                canUpdateRole={canUpdateRole}
              />
            </components.SingleValue>
          ),
          Menu: (props: MenuProps<ProjectRoleOption, false>) => (
            <Tailwind>
              <components.Menu {...props} className="w-[8rem]" />
            </Tailwind>
          ),
          Option: CheckmarkSelectOption,
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        isSearchable={false}
        isLoading={isUpdating}
        isDisabled={isUpdating}
      />
    ) : (
      <ProjectRoleLabel
        isProjectRoleDiffersFromTeamRole={isProjectRoleDiffersFromTeamRole}
        teamRoleIsViewer={teamRoleIsViewer}
        roleName={roleName}
        canUpdateRole={canUpdateRole}
      />
    );
  }
);

export type ProjectRoleOption = {
  value: string;
  role: string;
  isDisabled: boolean;
};

export const getProjectRoleOptionFromRole = (
  role: string
): ProjectRoleOption => ({
  role: role,
  value: getRoleText(role),
  isDisabled: false,
});

export function getProjectRoleOptionFromRoleOption(
  role: RoleOption
): ProjectRoleOption {
  return {
    role: role.value,
    value: getRoleText(role.value),
    isDisabled: role.isDisabled === true,
  };
}

export function CheckmarkSelectOption({
  children,
  ...props
}: OptionProps<ProjectRoleOption, false>) {
  return (
    <components.Option {...props} className="flex items-center justify-between">
      {children}
      {props.isSelected && <Icon name="checkmark" width={18} height={18} />}
    </components.Option>
  );
}

interface ProjectRoleLabelProps {
  isProjectRoleDiffersFromTeamRole: boolean;
  teamRoleIsViewer: boolean;
  roleName: string;
  canUpdateRole: boolean;
}

const ProjectRoleLabel: React.FC<ProjectRoleLabelProps> = ({
  isProjectRoleDiffersFromTeamRole,
  roleName,
  canUpdateRole,
  teamRoleIsViewer,
}) => {
  const tooltipContent = teamRoleIsViewer
    ? 'This role cannot be changed as the team role is viewer'
    : isProjectRoleDiffersFromTeamRole
      ? "This role has been modified from the user's team role"
      : '';

  return (
    <Tailwind className="flex h-full items-center">
      {tooltipContent !== '' ? (
        <Tooltip
          trigger={
            <div>
              <span
                className={`font-semibold ${
                  canUpdateRole ? 'text-moon-800' : 'text-moon-500'
                }`}>
                {roleName}
              </span>
            </div>
          }
          position="bottom left"
          content={tooltipContent}
        />
      ) : (
        <span
          className={`font-semibold ${
            canUpdateRole ? 'text-moon-800' : 'text-moon-500'
          }`}>
          {roleName}
        </span>
      )}
    </Tailwind>
  );
};
